import { DtpProgramIntake } from "./dtp/DtpProgramIntake";
import { DtpProductPage } from './dtp/DtpProductPage';
import { useEffect, useState, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { ClinicSiteContext, setClinicSite } from '../lib/clinicSite';
import { initializeSentry } from '../lib/sentry';
import { fetchTenantConfig } from '../config/tenant-runtime';
import { DtpLayout } from '../components/dtp/DtpLayout';
import { DtpProtectedRoute } from '../components/dtp/DtpProtectedRoute';
import { DtpShop } from './dtp/DtpShop';
import { DtpCheckout } from './dtp/DtpCheckout';
import { DtpOrders } from './dtp/DtpOrders';
import { DtpRefills } from './dtp/DtpRefills';
import { DtpOrderConfirmation } from './dtp/DtpOrderConfirmation';
import { DtpLearn } from './dtp/DtpLearn';
import { DtpLearnCategory } from './dtp/DtpLearnCategory';
import { DtpLearnFamily } from './dtp/DtpLearnFamily';
import DtpMembership from './dtp/DtpMembership';
import DtpPortal from './dtp/DtpPortal';
import { DtpHome } from './dtp/DtpHome';
import { DtpAccount } from './dtp/DtpAccount';
import { DtpResults } from './dtp/DtpResults';
import { DtpBilling } from './dtp/DtpBilling';
import { DtpReceipt } from './dtp/DtpReceipt';
import { DtpResultDetail } from './dtp/DtpResultDetail';
import { DtpMessages } from './dtp/DtpMessages';
import { DtpSupport } from './dtp/DtpSupport';
import { DtpAva } from './dtp/DtpAva';
import { DtpTreatments } from './dtp/DtpTreatments';
import { AnalyticsPageViews } from '../components/analytics/AnalyticsPageViews';

export function ClinicSiteApp({ site }: { site: ClinicSiteContext }) {
  if (!site.available) return <main className="max-w-xl mx-auto p-8 text-center"><h1 className="text-2xl font-semibold">This clinic site is temporarily unavailable</h1><p className="my-5">You can continue through the clinic’s standard Bloom address.</p><a className="text-blue-700 underline" href={site.fallbackUrl}>Continue to {site.clinicName}</a></main>;
  // Everything on a clinic's own domain is patient-facing.
  return <BrowserRouter><AnalyticsPageViews /><Routes>
    <Route element={<DtpLayout />}>
      <Route path="/" element={<DtpShop />} /><Route path="/shop" element={<DtpShop />} />
      <Route path="/products/:slug" element={<DtpProductPage />} />
      <Route path="/intake/:programId" element={<DtpProgramIntake />} />
      <Route path="/checkout" element={<DtpCheckout />} />
      <Route path="/portal" element={<DtpProtectedRoute><DtpHome /></DtpProtectedRoute>} />
      <Route path="/account" element={<DtpProtectedRoute><DtpAccount /></DtpProtectedRoute>} />
      <Route path="/orders" element={<DtpProtectedRoute><DtpOrders /></DtpProtectedRoute>} />
      <Route path="/billing" element={<DtpProtectedRoute><DtpBilling /></DtpProtectedRoute>} />
      <Route path="/billing/receipts/:receiptId" element={<DtpProtectedRoute><DtpReceipt /></DtpProtectedRoute>} />
      <Route path="/messages" element={<DtpProtectedRoute><DtpMessages /></DtpProtectedRoute>} />
      <Route path="/support" element={<DtpProtectedRoute><DtpSupport /></DtpProtectedRoute>} />
      <Route path="/assistant" element={<DtpProtectedRoute><DtpAva /></DtpProtectedRoute>} />
      <Route path="/treatments" element={<DtpProtectedRoute><DtpTreatments /></DtpProtectedRoute>} />
      <Route path="/refills" element={<DtpProtectedRoute><DtpRefills /></DtpProtectedRoute>} />
      <Route path="/orders/:orderId/confirmation" element={<DtpProtectedRoute><DtpOrderConfirmation /></DtpProtectedRoute>} />
      {/* Results gate themselves inline so a results link returns the patient
          to the report they tapped, not to the storefront. DtpLayout offers
          "My Results" on every storefront, so omitting these here made that
          link dead on a clinic's own domain. */}
      <Route path="/results" element={<DtpResults />} />
      <Route path="/results/:resultId" element={<DtpResultDetail />} />
    </Route>
    <Route path="/learn" element={<DtpLearn />} /><Route path="/learn/:categorySlug" element={<DtpLearnCategory />} />
    <Route path="/learn/:categorySlug/:familySlug" element={<DtpLearnFamily />} />
    <Route path="/membership" element={<DtpMembership />} /><Route path="/portal/membership" element={<DtpPortal />} />
    <Route path="*" element={<main className="p-8"><h1>Page not found</h1><Link to="/">Return to the clinic store</Link></main>} />
  </Routes></BrowserRouter>;
}

/** Resolve at the trusted API boundary before rendering any application routes. */
export function ClinicSiteBootstrap({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ loaded: boolean; site: ClinicSiteContext | null; error?: string }>({ loaded: false, site: null });
  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);
    fetch('/api/clinic-site/context', { signal: controller.signal, cache: 'no-store' }).then(async response => {
      if (!response.ok) throw new Error('Could not load this site');
      const result = await response.json();
      if (!result.success || !('data' in result)) throw new Error('Invalid site response');
      const site = result.data as ClinicSiteContext | null;
      // Checkout must use this deployment's runtime publishable key, just as
      // ordinary Bloom pages do. Never fall back to a build-time payment key.
      if (site?.available) await fetchTenantConfig({ signal: controller.signal, strict: true });
      if (!mounted) return;
      setClinicSite(site);
      if (!site) initializeSentry();
      if (site) document.title = site.clinicName;
      setState({ loaded: true, site });
    }).catch(() => { if (mounted) setState({ loaded: true, site: null, error: 'This site could not be loaded. Please refresh to retry.' }); })
      .finally(() => clearTimeout(timer));
    return () => { mounted = false; clearTimeout(timer); controller.abort(); };
  }, []);
  if (!state.loaded) return <main className="p-8" role="status">Loading… <button className="underline ml-3" onClick={() => location.reload()}>Reload</button></main>;
  if (state.error) return <main className="p-8" role="alert">{state.error}</main>;
  return state.site ? <ClinicSiteApp site={state.site} /> : <>{children}</>;
}
